import { cloneDeep, forEach, isEqual, sortBy, uniqBy } from 'lodash-es';
import {
  BookingType,
  IBooking,
  IInfoAtTime,
  ISectionLookup,
  ITableItem,
  ITableItemAbstract,
  ITableOptionValidityRef,
  ITableSectionList,
} from 'shared-types/index';
import SectionsService from 'external-services/sections-service/index';
import {
  ITableSeaterContainerProps,
  IHandlers,
  IProps,
  ManualJoinType, IIncompleteTableSeaterProps,
} from 'external-components/table-seater/types';
import { useCallback, useEffect, useState } from 'react';
import SectionLookupService from 'external-services/section-lookup-service/sectionLookup.service';
import fromEntries from 'object.fromentries';
import { keys } from 'ts-transformer-keys';
import DateUtilsService from 'external-services/date-utils-service';

const NS = 'TableSelectionService';
const propsKeys: string[] = keys<IProps>();
const containerPropsKeys: string[] = keys<ITableSeaterContainerProps>();

export interface ITableDateSubset {
  vaildTablesAtTime: ITableItem[];
  invalidTablesAtTime?: ITableItem[];
  vaidJoinsAtTime: ITableItem[];
  freeTablesAtTime: ITableItem[];
  bookingsAtTime?: unknown[];
}

class TableSelectionService {
  private bookingUpdateCallback: (...args: any) => Promise<IBooking>;

  public setup(bookingUpdateCallback: (...args: any) => Promise<IBooking>): void {
    this.bookingUpdateCallback = bookingUpdateCallback;
  }

  /**
   * Returns lists of tables with and without table joins, plus object literal with properties based on table ids
   * @param selectedTime - time data
   * @param tables - list of tables to add
   * @param sectionsLookup - only needs to be passed in if the order of the sections needs to be specific to the `order`
   *  property of sectionsLookup
   */
  public createTableSectionList(
    selectedTime: ITableDateSubset,
    tables: ITableItemAbstract[],
    sectionsLookup?: any,
    isStandby?: boolean,
  ): ITableSectionList {
    // @todo: see if there is a better way than using 'tableStatusLookUps' (maybe WeakMap). severity: low
    const tableStatusLookUps: any = {};

    // including invalid tables as well, so that they can have their "problemsReasons" messages passed on to table seater
    selectedTime.invalidTablesAtTime.forEach((table) => {
      const tableOpt: ITableOptionValidityRef = {
        free: false,
        valid: false,
        table,
      };
      tableStatusLookUps[table._id] = tableOpt;
    });

    // marks tables as free at a certain time
    selectedTime.freeTablesAtTime.forEach((table) => {
      if (!tableStatusLookUps[table._id]) {
        const tableOpt: ITableOptionValidityRef = {
          free: false,
          valid: false,
          table,
        };
        tableStatusLookUps[table._id] = tableOpt; // creates new lookup item if doesn't exist
      }
      tableStatusLookUps[table._id].free = true;
    });

    // marks tables as valid at a certain time
    selectedTime.vaildTablesAtTime.forEach((table) => {
      if (!tableStatusLookUps[table._id]) {
        const tableOpt: ITableOptionValidityRef = {
          free: false,
          valid: false,
          table,
        };
        tableStatusLookUps[table._id] = tableOpt; // creates new lookup item if doesn't exist
      }
      tableStatusLookUps[table._id].valid = true;
      tableStatusLookUps[table._id].free = true;
    });

    // creates list of tables (excluding table joins)
    let tablesList: ITableItemAbstract[] = [];
    tables.forEach((table) => {
      const tableOpt: ITableOptionValidityRef = tableStatusLookUps[table._id];
      const newTable: ITableItemAbstract = cloneDeep(table);
      if (tableOpt) {
        newTable.free = tableOpt.free;
        newTable.valid = tableOpt.valid;
        newTable.problemReasons = tableOpt.table.problemReasons;
      } else {
        newTable.free = false;
        newTable.valid = false;
      }

      tablesList.push(newTable);
    });

    // then orders by section
    if (sectionsLookup) {
      sectionsLookup = SectionsService.manageDuplicateOrder(sectionsLookup);
      tablesList = sortBy(tablesList, (t) => {
        if (sectionsLookup[t.sectionId]) {
          return sectionsLookup[t.sectionId].order;
        }
        return t.sectionId;
      });
    }

    const tableSelectionList = uniqBy(cloneDeep(tablesList), '_id');

    // now copies the tablesList and includes the table joins

    selectedTime.vaidJoinsAtTime.forEach((tableJoin, i) => {
      if (isStandby && tableJoin.walkinOnly) {
        tableJoin.free = false;
        tableJoin.valid = false;
      } else {
        tableJoin.free = true;
        // tableJoin.valid = true;
        tableJoin.valid = !tableJoin.problemReasons || tableJoin.problemReasons.length === 0;
      }
      tableJoin._id = 'join_' + i;



      /**
       * The `tablesList` property doesn't get added to the `selectedTime.vaidJoinsAtTime` items immediately.
       * It is added inside `bookingsCalculations.service` (about half way through `getBookingInfoAtTime` mega function),
       * which gets called around 4 times before it is added. So we don't add to the `tablesList` until `tableJoin.tablesList`
       * has been populated.
       * @todo: This seems pretty inefficient, thrashing 'getBookingInfoAtTime' so many times. Look into if there is a
       * way of skipping the calls until ready. severity: low
       */
      const hasTables = tableJoin.tablesList && tableJoin.tablesList.length;
      if (hasTables) {
        // if tables have been populated, pushes them to the top of their section in the list
        const indexOfFirstSection = tablesList.findIndex(
          (t) => t.sectionId === tableJoin.tablesList[0].sectionId
        );
        tablesList.splice(indexOfFirstSection, 0, cloneDeep(tableJoin));
      }
    });

    const tableSelectionListWithJoins = uniqBy(tablesList, '_id');

    return {
      // does not include table joins (only for 'manual join' list). _id starts with "table_"
      tableSelectionList,

      // includes table joins (to be used when 'manual join' is deselected). _id starts with "join_".
      tableSelectionListWithJoins,

      tableStatusLookUps,
    };
  }

  /**
   * Shared container logic for TableSeater and StandbyTableSeater containers
   */
  public getTableSeaterProps(all: IIncompleteTableSeaterProps & ITableSeaterContainerProps & IHandlers): {
    props: IProps;
    handlers: IHandlers;
  } {
    /**
     * Sorts 'all' into IServices, IProps and IContainerProps
     */
    const props: IProps = this.getObjectByKeys(all, propsKeys) as IProps;
    // @ts-ignore
    const booking = all.booking
    let isStandBy = false
    if (booking) {
      isStandBy = booking.bookingType === BookingType.StandbyList
    }
    const {
      selectedTime,
      tables,
      selectedTableIds,
      label,
      showEmptyTableValidationMessage,
      isWhiteTheme,
      useReversedColors,
      manualJoinType,
      isLockedBooking,
      handleUpdate,
    }: ITableSeaterContainerProps = this.getObjectByKeys(
      all,
      containerPropsKeys
    ) as ITableSeaterContainerProps;
    props.isWhiteTheme = isWhiteTheme || false;
    props.useReversedColors = useReversedColors || false;
    props.manualJoinType = manualJoinType || null;
    props.label = label || null;
    props.showEmptyTableValidationMessage = showEmptyTableValidationMessage || null;
    props.isLockedBooking = isLockedBooking;

    const [preselectedTableIds, setPreselectedTableIds] = useState<string[]>();
    props.preselectedTableIds = preselectedTableIds;

    const [loadingSectionsLookup, setLoadingSectionsLookup] = useState<boolean>(false);
    const [sectionsLookup, setSectionsLookup] = useState<ISectionLookup[]>();
    props.sectionsLookup = sectionsLookup;

    // tables without joins (for when 'manual joins' is selected)
    const [tableSelectionList, setTableSelectionList] = useState<ITableItem[]>([]);
    props.tableSelectionList = tableSelectionList;

    // tables with joins (only when 'manual joins' is deselected)
    const [tableSelectionListWithJoins, setTableSelectionListWithJoins] = useState<ITableItem[]>(
      []
    );
    props.tableSelectionListWithJoins = tableSelectionListWithJoins;

    const [isManualJoin, setIsManualJoin] = useState<boolean>(
      manualJoinType === ManualJoinType.forceManualJoin
    );

    const update = useCallback(() => {
      if (sectionsLookup && selectedTime?.freeTablesAtTime) {
        const lists = this.createTableSectionList(selectedTime, tables, sectionsLookup, isStandBy);
        const _tableSelectionList = lists.tableSelectionList;
        const _tableSelectionListWithJoins = lists.tableSelectionListWithJoins;

        setTableSelectionList(_tableSelectionList);
        setTableSelectionListWithJoins(_tableSelectionListWithJoins);
        setPreselectedTableIds(selectedTableIds); // must wait until after tableSelectionList/tableSelectionListWithJoins are set

        // if preselected tables exist, trigger update in parent component, as if they were manually selected
        if (selectedTableIds) {
          handleUpdate(selectedTableIds, _tableSelectionListWithJoins, isManualJoin);
        }
      }
    }, [selectedTime, tables, sectionsLookup, selectedTableIds, isManualJoin]);

    // const update = useCallback( debounce(_update, 1000), [selectedTime, tables, sectionsLookup, selectedTableIds, isManualJoin]);

    useEffect(() => {
      update();
    }, [selectedTime]);

    useEffect(() => {
      let shouldUpdate = true;

      if (sectionsLookup) {
        // if sectionsLookup is already set, we can call update immediately
        update();
      } else if (!loadingSectionsLookup) {
        setLoadingSectionsLookup(true);

        SectionLookupService.getSectionsLookUp()
          .then((sections: any) => {
            if (!shouldUpdate) return;

            setLoadingSectionsLookup(false);
            setSectionsLookup(sections);
            update();
          })
          .catch((err: string) => {
            if (!shouldUpdate) return;

            setLoadingSectionsLookup(false);
            console.error('error getSectionsLookUp', err);
          });
      }

      return () => {
        shouldUpdate = false;
      }; // clean up
    }, [!sectionsLookup]);

    // check to see if selectedTableIds is changed externally
    if (!isEqual(selectedTableIds, preselectedTableIds)) {
      setPreselectedTableIds(selectedTableIds);
    }

    /**
     * Handlers used to modify app state (via a service) from within the module's container.
     * Similar to redux mapDispatchToProps.
     */
    const handlers: IHandlers = {
      handleSelectedChanged: (
        selectedTableIds: string[],
        tables: ITableItem[],
        _isManualJoin: boolean
      ) => {
        setIsManualJoin(isManualJoin);
        setPreselectedTableIds(null); // clear the preselect if a manual selection has been made
        handleUpdate(selectedTableIds, tables, _isManualJoin);
      },
    };
    return { props, handlers };
  }

  /**
   * Returns a new object only with property names that exist in the provided `keys` property.
   * Exists within ReactUtilsService as well, but can't have this as dependency of TableSelection.service.ts due
   * to ES6 unit tests not relying on react/JSX code.
   */
  private getObjectByKeys(obj: Object, keys: string[]): Object {
    return fromEntries(Object.entries(obj).filter(([name, val]) => keys.includes(name)));
  }

  /**
   * Checks if conflicted bookings include deleted tables and adds them to the waitlist if there are no valid tables
   * left on the booking.
   * @returns {Promise<boolean>} - true if a change to the booking has occurred, false if nothing has happened
   */
  public addBookingsWithNoTablesToWaitList(
    bookingConflicts: IBooking[],
    deletedTables: ITableItem[]
  ): Promise<boolean> {
    return new Promise((resolve) => {
      let count = 0;

      if (!bookingConflicts || !bookingConflicts.length) {
        resolve(false);
        return;
      }

      const done = (hasChanges: boolean): void => {
        count++;
        if (count === bookingConflicts.length) {
          resolve(hasChanges);
        }
      };

      const saveBooking = (booking: IBooking, orgBooking: IBooking) => {
        const updateResult = this.bookingUpdateCallback(booking, orgBooking);
        if (typeof updateResult === 'boolean') {
          done(false);
        } else {
          updateResult
            .then(() => {
              done(true);
            })
            .catch((err: any) => {
              console.warn(NS, err);
              done(false);
            });
        }
      };

      bookingConflicts.forEach((booking) => {
        const checkTablesExist: boolean =
          (!!booking.tables || !!booking.tables.length) &&
          booking.bookingType !== BookingType.StandbyList;

        if (checkTablesExist) {
          const deletedTableIds: string[] = deletedTables.map(({ _id }) => _id);
          const remainingTables: ITableItem[] = booking.tables.filter(
            ({ _id }) => !deletedTableIds.includes(_id)
          );
          const tablesHaveChanged: boolean = remainingTables.length !== booking.tables.length;
          const hasValidTables: boolean = booking.tables.some(
            ({ _id }) => !deletedTableIds.includes(_id)
          );

          // console.log(tablesHaveChanged, hasValidTables)
          if (tablesHaveChanged || !hasValidTables) {
            const orgBooking = cloneDeep(booking);
            if (hasValidTables) {
              // if the primary table was deleted, set a new primary table
              const hasPrimaryTable = remainingTables.find(
                ({ _id }) => _id === booking.primaryTableId
              );
              if (!hasPrimaryTable) {
                booking.primaryTableId = remainingTables[0]._id;
              }
              booking.tables = remainingTables;
            } else {
              booking.bookingType = BookingType.WaitList;
              booking.tables = [];
            }
            saveBooking(booking, orgBooking);
          } else {
            done(false);
          }
        } else if (booking.bookingType !== BookingType.StandbyList) {
          const orgBooking = cloneDeep(booking);
          booking.bookingType = BookingType.WaitList;
          saveBooking(booking, orgBooking);
        } else {
          done(false);
        }
      });
    });
  }

  public getTableByTime(availableTime: ITableDateSubset): ITableItem {
    let table = this.getTableByCapacityAndPriority(availableTime.vaildTablesAtTime);
    if (!table) {
      table = this.getTableByCapacityAndPriority(availableTime.vaidJoinsAtTime);
    }

    return table;
  }

  public getTableByCapacityAndPriority(tables: ITableItemAbstract[], activePax?: number): ITableItem {
    const sortedTables = sortBy(tables, ['capacity', 'priority']);
    const filteredTables = activePax
      ? sortedTables.filter(t => activePax <= t.capacity && activePax >= t.minCapacity)
      : sortedTables;

    if (filteredTables?.length) {
      // get all the tables with same priority
      const priorityOfLowestCapacityTable = filteredTables[0].priority;
      const lowestCapacity = filteredTables[0].capacity;

      const samePriorityTables = [];
      for (let i = 0; i < filteredTables.length; i++) {
        const t = filteredTables[i];
        if (t.priority === priorityOfLowestCapacityTable && t.capacity === lowestCapacity) {
          samePriorityTables.push(t);
        } else {
          break; // break the loop early so it doesn't need to loop through every other table
        }
      }

      // Select a random one
      const randomIndex = Math.floor(Math.random() * samePriorityTables.length);
      return samePriorityTables[randomIndex];
    }

    return null;
  }

  public getSelectedTablesFromList(
    tablesToCheck: ITableItem[],
    selectedTableIds: string[]
  ): ITableItem[] {
    if (tablesToCheck) {
      return tablesToCheck.filter((table) => selectedTableIds.includes(table._id));
    } else {
      return [];
    }
  }

  public getSelectedTableIds(selectedTime: IInfoAtTime): string[] {
    const table = sortBy(selectedTime.vaildTablesAtTime, ['capacity', 'priority'])[0];
    let selectedTableIds: string[] = [];
    if (table) {
      selectedTableIds.push(table._id);
    } else {
      const validJoin = sortBy(selectedTime.vaidJoinsAtTime, ['capacity', 'priority'])[0];
      if (validJoin) {
        selectedTableIds = validJoin.tables;
      }
    }
    return selectedTableIds;
  }
}

const instance = new TableSelectionService();
export default instance as TableSelectionService;
